/**
 * Registre centralisé de tous les services API.
 *
 * Phase 3 — Services API — Pattern Factory
 *
 * Chaque service est créé via la factory `createService()` avec ses méthodes CRUD
 * standard + méthodes custom spécifiques.
 *
 * ⚠️  Les signatures sont 100% rétrocompatibles avec les anciens fichiers de service.
 *     Toutes les méthodes CRUD gardent les noms et l'ordre de paramètres existants :
 *       save(data), view(id), update(data, id), list(), remove(id)
 *
 * Utilisation :
 *   import { clientsService, commandesService } from '@/services'
 *   const clients = await clientsService.list()
 *   const client  = await clientsService.view(id)
 */
import { createService } from './createService'
import api from './apiClient'

// ══════════════════════════════════════════════
// 01Cell — Services CRUD
// ══════════════════════════════════════════════

export const adminClientsService = createService('/adminClients', {
  query (item, filter) {
    return api.get(`/adminClients/query/${encodeURIComponent(item)}?champ=${filter}`)
  },
})

export const appareilsService = createService('/appareils', {
  listByGroup (activeInactive) {
    return api.get(`/appareils/listByGroup/${activeInactive}`)
  },
  listByCompanie () {
    return api.get('/appareils/listByCompanie')
  },
  listByModele (compagnie) {
    return api.get(`/appareils/listByModele/${compagnie}`)
  },
  listByStockage (modele) {
    return api.get(`/appareils/listByStockage/${modele}`)
  },
  listByCouleur (stockage, modele) {
    return api.get(`/appareils/listByCouleur/${stockage}/${modele}`)
  },
  bulkUpdate (appareils) {
    return api.put('/appareils/bulkUpdate', appareils)
  },
  bulkCreate (appareils) {
    return api.post('/appareils/bulkCreate', appareils)
  },
})

export const clientsService = createService('/clients', {
  search (item, ifGetContrat) {
    return api.get(`/clients/search/${item}?ifGetContrat=${ifGetContrat}`)
  },
  query (item, filter) {
    return api.get(`/clients/query/${encodeURIComponent(item)}?champ=${filter}`)
  },
  queryTrue (filter) {
    return api.get(`/clients/queryTrue?champ=${filter}`)
  },
  uqamRetraite () {
    return api.get('/clients/uqamRetraite')
  },
})

export const commandesService = createService('/commandes', {
  search (item) {
    return api.get(`/commandes/search/${item}`)
  },
  responsableCodeFacturation (item) {
    return api.get(`/commandes/responsable_code_facturation/${item}`)
  },
  codeFacturationValidation (codeFacturation, compte) {
    return api.get(`/commandes/validation_code_facturation/${codeFacturation}/${compte}`)
  },
  confirmOrder (id, commande) {
    return api.put(`/commandes/confirmOrder/${id}`, commande)
  },
  openOrder () {
    return api.get('/commandes/openOrder')
  },
  confirmationRequest (id, commande) {
    return api.put(`/commandes/confirmationRequest/${id}`, commande)
  },
  migrateLine (id, commande) {
    return api.put(`/commandes/migrateLine/${id}`, commande)
  },
  ubrResponsableUqam () {
    return api.get('/commandes/ubrResponsableUqam')
  },
  ubrResponsable () {
    return api.get('/commandes/ubrResponsable')
  },
  timeUsed (comptoirId, date) {
    return api.get(`/commandes/timeUsed/${comptoirId}/${date}`)
  },
})

export const contratsService = createService('/contrats', {
  search (item, page = 1, limit = 10) {
    return api.get(`/contrats/search/${item}?page=${page}&limit=${limit}`)
  },
  validUnique (item) {
    return api.get(`/contrats/validUnique/${item}`)
  },
})

export const facturesService = createService('/factures', {
  search (item) {
    return api.get(`/factures/search/${item}`)
  },
  query (item, filter) {
    return api.get(`/factures/query/${encodeURIComponent(item)}?champ=${filter}`)
  },
  callsDetail (item) {
    return api.get(`/factures/callsDetail/${item}`)
  },
  autreList (item) {
    return api.get(`/factures/autre/${item}`)
  },
  autreCreate (facture) {
    return api.post('/factures/autre', facture)
  },
  paiementErreur () {
    return api.get('/factures/paiementErreur')
  },
  getRefund (factureId) {
    return api.get(`/factures/getRefund/${factureId}`)
  },
  refund (paiementId, montant) {
    return api.put(`/factures/refund/${paiementId}`, montant)
  },
  getPeriode () {
    return api.get('/factures/getPeriode')
  },
})

export const groupesService = createService('/groupes')

export const listService = createService('/listes', {
  search (item) {
    return api.get(`/listes/search/${item}`)
  },
})

export const messagesService = createService('/messages', {
  search (item) {
    return api.get(`/messages/search/${item}`)
  },
  multiple (messages) {
    return api.post('/messages/multiple', messages)
  },
  replace (noMessages, langue) {
    return api.get(`/messages/replace/${noMessages}/${langue}`)
  },
})

export const paiementsInscriptionService = createService('/inscription_paiements', {
  query (item, filter) {
    return api.get(`/inscription_paiements/query/${encodeURIComponent(item)}?champ=${filter}`)
  },
  queryAll (item, filter) {
    return api.get(`/inscription_paiements/queryAll/${encodeURIComponent(item)}?champ=${filter}`)
  },
})

export const paiementsService = createService('/paiements')

export const parametersService = createService('/parametres', {
  multiple (parameters) {
    return api.post('/parametres/multiple', parameters)
  },
  listAll () {
    return api.get('/parametres/listAll')
  },
  listObjects () {
    return api.get('/parametres/listObjects')
  },
  bulkUpdateObjects (modifiedInputs) {
    return api.put('/parametres/bulkUpdateObjects', modifiedInputs)
  },
})

export const togglesService = createService('/toggles')

// ══════════════════════════════════════════════
// 01Cell — Services custom (pas de CRUD standard)
// ══════════════════════════════════════════════

export const dashboardService = {
  header () { return api.get('/dashboard/header') },
  graph () { return api.get('/dashboard/graph') },
  graphBar () { return api.get('/dashboard/graphBar') },
  graphLine () { return api.get('/dashboard/graphLine') },
  detailsFactures (type) { return api.get(`/dashboard/detailsFactures/${type}`) },
  detailsContrats (type) { return api.get(`/dashboard/detailsContrats/${type}`) },
  mauvaisPayeur () { return api.get('/dashboard/mauvaisPayeur') },
  exEmployeC2 () { return api.get('/dashboard/exEmployeC2') },
  fraisAjustement () { return api.get('/dashboard/fraisAjustement') },
}

export const facturationsService = {
  sommaire () { return api.get('/facturation/sommaire') },
  integrite () { return api.get('/facturation/integrite') },
  creerfactures (periode, fournisseur) { return api.get(`/facturation/creerfactures/${periode}/${fournisseur}`) },
  facturationMensuelle () { return api.get('/facturation/facturationMensuelle') },
  payerFactureManuelleToken (id) { return api.get(`/facturation/payerFactureManuelleToken/${id}`) },
  payerFactureManuelleCard (card, factureId) {
    return api.post(`/facturation/payerFactureManuelleCard/${factureId}`, card)
  },
  creerlot (periode, fournisseur) { return api.get(`/facturation/creerlot/${periode}/${fournisseur}`) },
  updateFacture (facture, factureId) {
    return api.put(`/facturation/updateFacture/${factureId}`, facture)
  },
  getFactureHtml (factureId) { return api.get(`/facturation/getFactureHtml/${factureId}`) },
  mauvaisPayeur () { return api.get('/facturation/mauvaisPayeur') },
  sendEmailmauvaisPayeur () { return api.get('/facturation/sendEmailmauvaisPayeur') },
  updateLotErrorUbr (oldUbr, newUbr) {
    return api.put(`/facturation/updateLotErrorUbr/${oldUbr}/${newUbr}`)
  },
  getErreurUbr () { return api.get('/facturation/getErreurUbr') },
  listTransactions (id) { return api.get(`/facturation/listTransactions/${id}`) },
}

export const reportService = {
  view () { return api.get('/report') },
  viewResponsable () { return api.get('/report/responsable') },
  clientFacture (annee, contratId) {
    return api.get(`/report/clientFacture/${annee}/${contratId}`)
  },
  responsableUbrFacture (periode, value, ubrOrgroup) {
    return api.get(`/report/responsableUbrFacture/${periode}/${value}/${ubrOrgroup}`)
  },
}

export const toolsService = {
  infoUser (data) { return api.get(`/tools/infoUser/${encodeURIComponent(data)}`) },
}

// ══════════════════════════════════════════════
// Services racine
// ══════════════════════════════════════════════

export const blanksService = createService('/blanks')

export const mailerService = {
  sendMail (options) { return api.post('/mailer', options) },
  sendEmailToCustomerWithMessageNo (messageNo) {
    return api.get(`/mailer/sendEmailToCustomerWithMessageNo/${messageNo}`)
  },
}

export const roleService = createService('/roles')

export const s3Service = {
  getSignedUrl (params) { return api.post('/aws-s3/getSignedUrl', params) },
  deleteObject (bucket, key) { return api.delete(`/aws-s3/deleteObject/${bucket}?key=${key}`) },
}

export const utilisateurService = createService('/utilisateurs', {
  query (utilisateur) {
    return api.get(`/utilisateurs/query/${utilisateur}`)
  },
})

// ══════════════════════════════════════════════
// STRIPE
// ══════════════════════════════════════════════

export const stripeAccountService = {
  save () { return api.post('/stripe/accounts') },
  update (account) { return api.put('/stripe/accounts', account) },
  view () { return api.get('/stripe/accounts') },
  createAccountLink (account) { return api.post('/stripe/createAccountLink', account) },
}

export const stripeBalanceTransactionsService = {
  view (balanceTransactionId) { return api.get(`/stripe/balanceTransactions/${balanceTransactionId}`) },
}

export const stripeCardService = {
  getApiPublic () { return api.get('/stripe/card/apiSecret') },
  createSession (session) { return api.post('/stripe/card/createSession', session) },
  retriveSessionNewCard (sessionId) {
    return api.get(`/stripe/card/retriveSessionNewCard/${sessionId}`)
  },
  retriveSessionPaiement (sessionId) {
    return api.get(`/stripe/card/retriveSessionPaiement/${sessionId}`)
  },
  remove (cardId, clientId) { return api.delete(`/stripe/card/${cardId}/${clientId}`) },
}

export const stripeChargeService = {
  list (startingAfter) { return api.get(`/stripe/charges/list/${startingAfter}`) },
  view (chargeId) { return api.get(`/stripe/charges/view/${chargeId}`) },
  queryByFactureId (factureId) { return api.get(`/stripe/charges/queryByFactureId/${factureId}`) },
  queryByCustomerId (customerId, nextPage) {
    return api.get(`/stripe/charges/queryByCustomerId/${customerId}/${nextPage}`)
  },
  queryAllByCustomerId (customerId) {
    return api.get(`/stripe/charges/queryAllByCustomerId/${customerId}`)
  },
  queryCustomerId (nextPage) { return api.get(`/stripe/charges/queryCustomerId/${nextPage}`) },
  queryByPeriod (periode, nextPage) {
    return api.get(`/stripe/charges/queryByPeriod/${periode}/${nextPage}`)
  },
  queryByPaymentIntent (paimentIntentId) {
    return api.get(`/stripe/charges/queryByPaymentIntent/${paimentIntentId}`)
  },
}

export const stripeCustomerService = {
  list (startingAfter) { return api.get(`/stripe/customers/list/${startingAfter}`) },
  save (customer) { return api.post('/stripe/customers', customer) },
  view (customerId) { return api.get(`/stripe/customers/${customerId}`) },
  update (customerId, customer) { return api.put(`/stripe/customers/${customerId}`, customer) },
  card (customerId) { return api.get(`/stripe/customers/cards/${customerId}`) },
  search (clientId) { return api.get(`/stripe/customers/search/${clientId}`) },
  searchByCustomer (data, nextPage) {
    return api.get(`/stripe/customers/searchByCustomer/${encodeURIComponent(data)}/${nextPage}`)
  },
}

export const stripePaiementMethodeService = {
  view (paimentMethodId) { return api.get(`/stripe/paymentsMethod/${paimentMethodId}`) },
}

export const stripePaiementService = {
  searchByFactureId (factureId) { return api.get(`/stripe/payments/searchByFactureId/${factureId}`) },
  queryByFactureId (factureId) { return api.get(`/stripe/payments/queryByFactureId/${factureId}`) },
  view (paimentIntentId) { return api.get(`/stripe/payments/${paimentIntentId}`) },
}

export const stripeRefundService = {
  queryByPaymentIntent (paimentIntentId) {
    return api.get(`/stripe/refunds/queryByPaymentIntent/${paimentIntentId}`)
  },
}

// ══════════════════════════════════════════════
// PAYFACTO
// ══════════════════════════════════════════════

export const payFactoService = {
  list (clientId) { return api.get(`/payfacto/card/list/${clientId}`) },
  save (card, clientId) { return api.post(`/payfacto/card/create/${clientId}`, card) },
  view (cardId, clientId) { return api.get(`/payfacto/card/${cardId}/${clientId}`) },
  update (profileCard, cardId, clientId) {
    return api.put(`/payfacto/card/${cardId}/${clientId}`, profileCard)
  },
  remove (cardId, clientId) { return api.delete(`/payfacto/card/${cardId}/${clientId}`) },
  getForm () { return api.get('/payfacto/getForm') },
}

// ══════════════════════════════════════════════
// GLOBALPAYMENTS
// ══════════════════════════════════════════════

export const globalPaymentsCardService = {
  list (clientId) { return api.get(`/globalpayments/card/list/${clientId}`) },
  save (card, clientId) { return api.post(`/globalpayments/card/create/${clientId}`, card) },
  view (cardId, clientId) { return api.get(`/globalpayments/card/${cardId}/${clientId}`) },
  update (profileCard, cardId, clientId) {
    return api.put(`/globalpayments/card/${cardId}/${clientId}`, profileCard)
  },
  remove (cardId, clientId) { return api.delete(`/globalpayments/card/${cardId}/${clientId}`) },
}

// ══════════════════════════════════════════════
// PAYSAFE
// ══════════════════════════════════════════════

export const paysafeProfileService = createService('/paysafe/profile')

export const paysafeProfileAddressService = {
  list () { return api.get('/paysafe/profile/address') },
  save (profileAddress, profileId) {
    return api.post(`/paysafe/profile/address/${profileId}`, profileAddress)
  },
  view (profileAddressId, profileId) {
    return api.get(`/paysafe/profile/address/${profileAddressId}/${profileId}`)
  },
  update (profileAddress, profileAddressId, profileId) {
    return api.put(`/paysafe/profile/address/${profileAddressId}/${profileId}`, profileAddress)
  },
  remove (profileAddressId, profileId) {
    return api.delete(`/paysafe/profile/address/${profileAddressId}/${profileId}`)
  },
}

export const paysafeProfileCardService = {
  list () { return api.get('/paysafe/profile/card') },
  save (profileCard, profileId, clientId) {
    return api.post(`/paysafe/profile/card/create/${profileId}/${clientId}`, profileCard)
  },
  view (profileCardId, profileId) {
    return api.get(`/paysafe/profile/card/${profileCardId}/${profileId}`)
  },
  update (profileCard, profileCardId, profileId, clientId) {
    return api.put(`/paysafe/profile/card/${profileCardId}/${profileId}/${clientId}`, profileCard)
  },
  remove (profileCardId, profileId, clientId) {
    return api.delete(`/paysafe/profile/card/${profileCardId}/${profileId}/${clientId}`)
  },
  validation (cardValidation) { return api.post('/paysafe/profile/card/validation', cardValidation) },
}

// ══════════════════════════════════════════════
// UQAM
// ══════════════════════════════════════════════

export const usersManagementService = {
  // Roles
  listRoles () { return api.get('/uqam/usersmanagenent/roles') },
  addRoleUsers (user) { return api.post('/uqam/usersmanagenent/roles/', user) },
  viewRoleUsers (roleId) { return api.get(`/uqam/usersmanagenent/roles/users/${roleId}`) },
  deleteRoleUsers (roleId, userId) {
    return api.delete(`/uqam/usersmanagenent/roles/delete/users/${roleId}/${userId}`)
  },
  // Users
  listUser (user) { return api.get(`/uqam/usersmanagenent/users/?data=${user}`) },
  viewUser (userId) { return api.get(`/uqam/usersmanagenent/users/${userId}`) },
  deleteAllUserRoles (userId) { return api.delete(`/uqam/usersmanagenent/users/${userId}`) },
}

// ══════════════════════════════════════════════
// Alias PascalCase — Rétrocompatibilité avec les imports existants
// Permet de migrer en changeant seulement le chemin d'import :
//   import { CommandesService } from '@/services'
// ══════════════════════════════════════════════

// 01Cell
export { adminClientsService as AdminClientsService }
export { appareilsService as AppareilsService }
export { clientsService as ClientsService }
export { commandesService as CommandesService }
export { contratsService as ContratsService }
export { dashboardService as DashboardService }
export { facturationsService as FacturationsService }
export { facturesService as FacturesService }
export { groupesService as GroupesService }
export { listService as ListService }
export { messagesService as MessagesService }
export { paiementsInscriptionService as PaiementsInscriptionService }
export { paiementsService as PaiementsService }
export { parametersService as ParametersService }
export { reportService as Report }
export { togglesService as TogglesService }
export { toolsService as ToolsService }

// Racine
export { blanksService as BlanksService }
export { mailerService as MailerService }
export { roleService as RoleService }
export { s3Service as S3 }
export { utilisateurService as UtilisateurService }

// Stripe
export { stripeAccountService as StripeAccountService }
export { stripeBalanceTransactionsService as StripeBalanceTransactionsService }
export { stripeCardService as StripeCardService }
export { stripeChargeService as StripeChargeService }
export { stripeCustomerService as StripeCustomerService }
export { stripePaiementMethodeService as StripePaiementMethodeService }
export { stripePaiementService as StripePaiementService }
export { stripeRefundService as StripeRefundService }

// PayFacto
export { payFactoService as PayFactoService }

// GlobalPayments
export { globalPaymentsCardService as GlobalPaymentsCardService }

// Paysafe
export { paysafeProfileService as PaysafeProfileService }
export { paysafeProfileAddressService as PaysafeProfileAddressService }
export { paysafeProfileCardService as PaysafeProfileCardService }

// UQAM
export { usersManagementService as UsersManagementService }
